import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SearchResult, SearchResultGroup, SearchResultType, HighlightPipe } from '@coin/shared/group-search/util';
import { FeatureFlagsService, LoadingScreenComponent } from '@coin/shared/core/data-access';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { SearchState } from '../../state/search.state';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { V2ButtonComponent } from '@coin/shared/core/ui-storybook';
import { FeatureFlagDirective } from '@coin/shared/core/feature-directives';

@Component({
  selector: 'coin-group-search-results',
  templateUrl: './group-search-results.component.html',
  styleUrls: ['./group-search-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SearchState],
  imports: [V2ButtonComponent, FeatureFlagDirective, LoadingScreenComponent, AsyncPipe, TranslatePipe, HighlightPipe]
})
export class GroupSearchResultsComponent implements OnInit, OnChanges {
  private readonly searchState = inject(SearchState);
  private readonly router = inject(Router);
  private readonly featureFlagService = inject(FeatureFlagsService);

  public readonly searchString = input.required<string, string>({ transform: (s: string) => s?.trim() });
  readonly searchResultGroups = input.required<SearchResultGroup[]>();
  readonly resultCacheKey = input<string>(undefined);
  readonly isCosmos = input<boolean>(true);
  @Output() navigatedToResult = new EventEmitter();
  @Output() initializeSearchLambda = new EventEmitter();

  @ViewChildren('searchResult') public searchResults: QueryList<ElementRef>;

  private readonly searchTrigger$ = new Subject<string>();

  recentSearchResults$ = this.searchState.recentSearchResults$;

  constructor() {
    this.searchTrigger$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(searchString => {
        if (searchString) {
          this.searchState.loadSearchResults(searchString);
          this.searchResultGroups()?.forEach(group => group.loadResults(searchString));
        } else {
          this.searchState.resetSearchResults();
          this.searchResultGroups()?.forEach(group => group.resetSearchResults());
        }
      });
    this.initializeSearchLambda.emit();
  }

  public ngOnInit(): void {
    const resultCacheKey = this.resultCacheKey();
    if (resultCacheKey) {
      this.searchState.loadRecentSearchResults(this.searchResultGroups(), resultCacheKey);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchString) {
      this.searchTrigger$.next(changes.searchString.currentValue);
    }

    if (changes.searchResultGroups && !changes.searchResultGroups.isFirstChange()) {
      throw new Error('result groups cannot be changed after init');
    }
  }

  public navigateToResult(result: SearchResult, saveInRecentSearchResults: boolean, urlPart = ''): void {
    const destUrl =
      this.featureFlagService.isActive('inCompanyView') && this.isCosmos()
        ? [...result.routerLink, urlPart]
        : result.routerLink;
    this.router.navigate(destUrl, { queryParams: result.queryParams, queryParamsHandling: result.queryParamsHandling });

    this.navigatedToResult.emit();

    const resultCacheKey = this.resultCacheKey();
    if (resultCacheKey && saveInRecentSearchResults) {
      this.searchState.saveRecentSearchResult(result, resultCacheKey);
    }
  }

  public isFilterActive(filter: SearchResultType): boolean {
    return this.searchState.isFilterActive(filter);
  }

  public showSearchResults(resultType: SearchResultType): boolean {
    return this.searchState.showSearchResults(resultType);
  }

  public toggleFilter(filter: SearchResultType): void {
    if (this.isFilterActive(filter)) {
      this.searchState.setSelectedFilter(undefined);
    } else {
      this.searchState.setSelectedFilter(filter);
    }
  }

  public loadMoreResults(resultType: SearchResultType): void {
    this.searchResultGroups()
      .find(group => group.resultType === resultType)
      .loadMoreResults(this.searchState.currentSearchString);

    // Focus last element to keep focus in search results
    this.getLastSearchResult(resultType).nativeElement.focus();
  }

  private getLastSearchResult(resultType: SearchResultType): ElementRef {
    const { titleKey } = this.searchResultGroups().find(group => group.resultType === resultType);
    const filteredResults = this.searchResults.filter(result => result.nativeElement.name === titleKey);
    return filteredResults.pop();
  }
}
